<template>
  <div>
    <PageHeader title="Create Contract" showBack />
    <div class="card p-4">
      <b-form @submit.prevent="handleCreate">
        <b-row>
          <b-col md="6">
            <b-form-group label="Title" label-class="form-label">
              <b-form-input v-model="form.title" placeholder="Contract title" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Template" label-class="form-label">
              <b-form-select v-model="selectedTemplate" :options="templateOptions" @change="loadTemplateContent" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Contact" label-class="form-label">
          <v-select v-model="form.contact" :options="contacts" label="text" :reduce="c => c.value" placeholder="Select contact..." />
        </b-form-group>

        <template v-if="!selectedTemplatePdf">
          <b-form-group label="Contract Content" label-class="form-label">
            <editor v-model="form.content_html" :api-key="tinymceKey" :init="{ height: 400, menubar: true, plugins: 'lists link table code', toolbar: 'undo redo | bold italic | bullist numlist | code' }" />
          </b-form-group>
        </template>

        <div class="mt-3">
          <b-button type="submit" variant="primary" :disabled="isSaving">{{ isSaving ? 'Creating...' : 'Create Contract' }}</b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PageHeader from '../../components/Ui/PageHeader.vue';
import Editor from '@tinymce/tinymce-vue';

export default {
  name: 'CreateContract',
  components: { PageHeader, Editor },
  data() {
    return {
      form: { title: '', template: null, contact: null, content_html: '', is_pdf_based: false },
      selectedTemplate: null, templates: [], contacts: [], isSaving: false,
      tinymceKey: process.env.VUE_APP_TINYMCE_API_KEY,
    };
  },
  computed: {
    templateOptions() {
      const sorted = [...this.templates].sort((a, b) => {
        if (a.is_pdf_template && !b.is_pdf_template) return -1;
        if (!a.is_pdf_template && b.is_pdf_template) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      return [
        { value: null, text: 'Blank (no template)' },
        ...sorted.map(t => ({ value: t.id, text: t.name + (t.is_pdf_template ? ' (PDF)' : ' (HTML)') })),
      ];
    },
    selectedTemplatePdf() {
      if (!this.selectedTemplate) return false;
      const t = this.templates.find(t => t.id === this.selectedTemplate);
      return t && t.is_pdf_template;
    },
  },
  methods: {
    ...mapActions('contract', ['createContract']),
    ...mapActions('contractTemplate', ['getAllContractTemplates', 'getContractTemplate']),
    ...mapActions('contact', ['getAllContacts']),
    async loadTemplateContent() {
      if (!this.selectedTemplate) {
        this.form.content_html = '';
        this.form.template = null;
        this.form.is_pdf_based = false;
        return;
      }
      try {
        const res = await this.getContractTemplate(this.selectedTemplate);
        const tpl = res.data;
        this.form.content_html = tpl.content_html || '';
        this.form.template = this.selectedTemplate;
        this.form.is_pdf_based = !!tpl.is_pdf_template;
      } catch (err) { /* ignore */ }
    },
    async handleCreate() {
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        const res = await this.createContract({ ...this.form, school: user.school.id });
        const contractId = res.data?.data?.id || res.data?.id;
        this.makeToast('success', 'Contract created');
        if (contractId) {
          this.$router.push({ name: 'contract-detail', params: { id: contractId } });
        } else {
          this.$router.push({ name: 'contracts-list' });
        }
      } catch (err) { this.makeToast('danger', 'Failed to create'); }
      finally { this.isSaving = false; }
    },
  },
  async created() {
    try {
      const [tRes, cRes] = await Promise.all([this.getAllContractTemplates(), this.getAllContacts({ limit: 1000 })]);
      this.templates = tRes.data.results || tRes.data || [];
      const contactData = cRes.data.results || cRes.data || [];
      this.contacts = contactData.map(c => ({ value: c.id, text: `${c.first_name} ${c.last_name}` }));
    } catch (err) { /* ignore */ }
  },
};
</script>
